import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for New Content) ---
import {
    // Icons mapped to the 9 operational points:
    faClipboardCheck, // 1. Operational Audits
    faChartLine,      // 2. Market & Competitor Analysis
    faBullhorn,       // 3. Marketing & Brand Positioning
    faLaptop,         // 4. Website Design & Development
    faHandshake,      // 5. Public Relations & Community Engagement
    faDatabase,       // 6. Hospital Information Systems (HIS)
    faFileMedical,    // 7. Medical Records Management
    faClipboardList,  // 8. Standard Operating Procedures (SOPs)
    faChalkboardTeacher, // 9. Training & Development
    // Keep other potentially useful icons if needed, or remove if strictly unused
    // faQuoteLeft, // Example: if a quote section were added back
    // Icons likely unused from previous version (can be removed or commented out):
    /*
    faMapMarkedAlt, faDraftingCompass, faBuilding, faCogs, faCube,
    faProjectDiagram, faLeaf, faGlobeAmericas, faRulerCombined,
    faProcedures, faUsersCog, faStethoscope, faSitemap, faObjectGroup,
    faPencilRuler, faTasks, faSyncAlt, faLightbulb,
    faShieldAlt, faTruck, faUsers, faFolderOpen, faCheckCircle, faChartBar
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons

// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    // Ensure parent exists and children can be accessed before calculating index
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        // Target prefixed class
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.3 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "Bespoke Solutions" section (Features)
    // const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Removed "Why Choose Us" section
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // For "Operational Consistency & Support" section (Detailed Planning)
    // const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Unused
    const [title5Ref, title5Visible] = useIntersectionObserverTitle(titleObserverOptions); // For Methodology Section
    const [title6Ref, title6Visible] = useIntersectionObserverTitle(titleObserverOptions); // For Results Section

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Keeping original hero image, but can be changed if needed
    const heroBgImage = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80';

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Excellence in Healthcare Operations & Management Consulting</h1>
            <p className="cliniqor-sub-heading">
              Transforming Healthcare Delivery
            </p>
            <p className="cliniqor-intro-text">
              In the rapidly evolving healthcare industry, optimizing hospital operations and management processes is essential for providing superior patient care, ensuring compliance, and achieving long-term growth. Cliniqor is committed to helping healthcare providers elevate their operational standards through tailored solutions that enhance efficiency, improve patient experiences, and support sustainable development.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO SECTION --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure for layout consistency */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>Driving Impactful Change</h3>
                     <p>Our team of seasoned experts specializes in streamlining hospital operations, optimizing patient care delivery, and driving impactful change for healthcare organizations across the globe.</p>
                     <p>At Cliniqor, we recognize that each healthcare facility has its unique set of challenges, operational intricacies, and opportunities. Our consulting solutions are tailored to address these needs comprehensively, providing actionable strategies that ensure operational excellence, superior care delivery, and a competitive advantage.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
                 {/* Removed old quote box */}
             </div>
          </div>
        </section>

        {/* --- CORE SERVICES / FEATURES SECTION (Points 1-5) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            {/* Apply prefixed visible class based on observer state */}
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Bespoke Healthcare Operations & Management Solutions</h2>
              <p>Tailored strategies addressing key operational and market challenges.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained */}
              {/* --- NEW FEATURE CARDS (Mapped from numbered sections 1-5) --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClipboardCheck} /></div>
                    <h4>1. Comprehensive Operational Audits & Process Optimization</h4>
                    <p>In-depth analysis to identify inefficiencies, streamline processes, and optimize resource allocation.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartLine} /></div>
                    <h4>2. Strategic Market & Competitor Analysis</h4>
                    <p>Informed decision-making through market trends, benchmarking, and differentiation strategies.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBullhorn} /></div>
                    <h4>3. Cutting-Edge Marketing & Brand Positioning</h4>
                    <p>Blending traditional and digital campaigns to enhance brand image and maximize patient acquisition.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faLaptop} /></div>
                    <h4>4. Exceptional Website Design & Development</h4>
                    <p>Bespoke, functional websites to enhance patient engagement and streamline interactions.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faHandshake} /></div>
                    <h4>5. Strengthening Public Relations & Community Engagement</h4>
                    <p>Building trust and positive public image through strategic PR and CSR initiatives.</p>
                </div>
              </div>
              {/* Removed the extra cards from the previous version (cards 6-9) */}
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- WHY CHOOSE US SECTION REMOVED --- */}
        {/*
         <section className="cliniqor-section cliniqor-section-bg-dark cliniqor-why-choose-us-section">
             ... content removed ...
         </section>
        */}

        {/* --- OPERATIONAL CONSISTENCY / SUPPORT SERVICES SECTION (Points 6-9) --- */}
        {/* Replaced old "Detailed Planning" with the second block of numbered points */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Operational Consistency & Support Systems</h2>
               <p>Integrating technology, standardizing procedures, and empowering staff for optimal performance.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-planning-items-wrapper"> {/* Kept existing wrapper class */}
                 {/* --- NEW PLANNING ITEMS (Mapped from second numbered block 6-9) --- */}
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDatabase} /></div>
                    <div className="cliniqor-content"><h4>6. Seamless Integration of Advanced HIS</h4> <p>Consultancy for selecting and implementing systems for efficiency and data security.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faFileMedical} /></div>
                    <div className="cliniqor-content"><h4>7. Comprehensive Medical Records Management</h4> <p>Developing efficient digital systems for record-keeping, storage, and retrieval.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <div className="cliniqor-content"><h4>8. Standard Operating Procedures (SOPs)</h4> <p>Developing and refining SOPs for seamless workflows and standardized practices.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChalkboardTeacher} /></div>
                    <div className="cliniqor-content"><h4>9. Tailored Training & Development Programs</h4> <p>Upskilling staff to minimize errors, improve performance, and provide exceptional care.</p> </div>
                 </div>
                 {/* Removed extra items from previous version (items 5-7) */}
                 {/* --- END NEW PLANNING ITEMS --- */}
            </div>
          </div>
        </section>

        {/* --- METHODOLOGY SECTION --- */}
        <section className="cliniqor-section cliniqor-section-bg-light"> {/* Reusing a background style */}
           <div className="cliniqor-container">
              <div ref={title5Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title5Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Cliniqor’s Proven Methodology</h2>
                 <p>A Holistic Approach to Healthcare Transformation</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up"> {/* Simple text container */}
                   <p>Cliniqor’s methodology is built on a foundation of data-driven decision-making, strategic insight, and a commitment to continuous improvement. We start by conducting a detailed assessment of your hospital’s current operations, followed by the development of tailored solutions that enhance efficiency, optimize patient care, and drive positive outcomes.</p>
                   <p>Our consultants work in close collaboration with your leadership team to implement these strategies, measure progress, and ensure long-term success through comprehensive audits, custom planning, and continuous performance evaluation.</p>
              </div>
           </div>
        </section>

        {/* --- RESULTS SECTION --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title6Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title6Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Delivering Unmatched Results</h2>
                 <p>Expertise You Can Count On</p>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up"> {/* Simple text container */}
                   <p>With a deep understanding of the healthcare sector and a proven track record of success, Cliniqor ensures that every strategy we implement leads to measurable improvements. Our team provides hands-on support, fills operational gaps, and drives impactful change to ensure that hospitals achieve operational excellence while enhancing patient care and satisfaction.</p>
                   <p>We assign the right experts to each project, providing comprehensive consulting, ongoing monitoring, and clear performance reporting to ensure we consistently meet your objectives within the agreed timeline and budget, achieving key operational milestones and KPIs.</p>
              </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Elevate Your Healthcare Operations with Cliniqor</h3>
          <p>Partner with us to streamline processes, enhance patient experiences, and drive sustainable growth for your healthcare organization. Let's discuss how our expertise can transform your operations.</p>
          {/* --- END UPDATED FOOTER CONTENT --- */}
          <a href="#contact" className="cliniqor-cta-button">Request a Consultation</a> {/* Button kept */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;